import { NgOptimizedImage, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  forwardRef,
  Input,
  OnInit,
  SecurityContext,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DocServerService } from '@compiere-ws/services/doc-server/doc-server.service';
import { Global } from '@iupics-manager/models/global-var';
import PrimeOverlayComponent from '@iupics-components/overrided/prime-overlay/prime-overlay.component';
import { XlsxParser } from '@iupics-manager/managers/xlsx-parser/xlsx-parser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as mime from 'mime';
import { OverlayPanel } from 'primeng/overlaypanel';
import ModalUiComponent from '../layouts/modal-ui/modal-ui.component';
import PdfViewerComponent from '../pdf-viewer/pdf-viewer.component';
import XmlViewerComponent from '../xml-viewer/xml-viewer.component';

// Extensions the viewer can render inline (images are detected by mime type)
const RENDERABLE_EXTENSIONS = ['pdf', 'xml', 'sh', 'txt', 'csv', 'xlsx'];

/** Font Awesome class matching a file name, used by the preview header and fallback. */
export function getFileTypeIconClass(fileName: string): string {
  const ext = fileName && fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
  const mimeType = ext ? mime.getType(ext) : null;
  if (ext === 'pdf') {
    return 'icon-pdf';
  }
  if (mimeType?.startsWith('image')) {
    return 'icon-img';
  }
  if (ext === 'csv') {
    return 'icon-csv';
  }
  if (['xls', 'xlsx', 'ods'].includes(ext)) {
    return 'icon-excel';
  }
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) {
    return 'icon-word';
  }
  if (['ppt', 'pptx', 'odp'].includes(ext)) {
    return 'fa-solid fa-file-powerpoint';
  }
  if (ext === 'html') {
    return 'icon-html';
  }
  if (ext === 'sql') {
    return 'icon-sql';
  }
  if (['xml', 'json', 'sh', 'js'].includes(ext)) {
    return 'fa-solid fa-file-code';
  }
  if (['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) {
    return 'icon-zip';
  }
  if (mimeType?.startsWith('video')) {
    return 'fa-solid fa-file-video';
  }
  if (mimeType?.startsWith('audio')) {
    return 'fa-solid fa-file-audio';
  }
  if (['txt', 'log', 'md'].includes(ext)) {
    return 'icon-txt';
  }
  return 'icon-file';
}

@Component({
  selector: 'iu-preview-doc',
  templateUrl: './preview-doc.component.html',
  styleUrls: ['./preview-doc.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    forwardRef(() => ModalUiComponent),
    NgStyle,
    NgOptimizedImage,
    TranslateModule,
    NgTemplateOutlet,
    NgOptimizedImage,
    PrimeOverlayComponent,
    PdfViewerComponent,
    XmlViewerComponent,
  ],
})
export default class PreviewDocComponent implements OnInit, AfterViewInit {
  @Input() modal = false;
  @Input() urlFile;
  @Input() fileName;
  isPreviewDisplayed = false;
  isImagePreview = false;
  isDocPreview = false;
  blob: Blob;
  @Input() url: SafeUrl;
  dataUrl: any;
  extension: string;
  @Input() modalStyle = null;
  @Input() imgStyle = { 'max-width': '100%', height: 'max-content' };
  mediaType = 'application/pdf';
  showError = false;
  @Input() fromDocServer = true;
  @Input() appendTo = 'body';
  hasPreviewError = false;
  @ViewChild('opRef', { static: false }) opRef: OverlayPanel;
  @Input() overlay = false;
  @Input() parentComponent: any;
  text;
  csvContent;
  innerHtmlContent;
  @ViewChild('modalRef', { static: false }) modalRef: ModalUiComponent;
  overridePreviewPanelStyle: string[][];
  /** true while the document is fetched and decoded */
  isLoading = false;
  /** file type the viewer cannot render inline */
  isUnsupported = false;
  imageZoomed = false;
  /** url given to onPreview, kept for download */
  sourceUrl: any;
  fileIconClass = 'icon-file';
  // ignores late replies when another file was opened meanwhile
  private previewSeq = 0;

  constructor(
    private docServerService: DocServerService,
    private sanitized: DomSanitizer,
    private translateService: TranslateService,
    private xlsxParser: XlsxParser
  ) {}

  ngOnInit(): void {
    const builedStyle = {
      'iu-modal-body': { 'align-items': 'center', height: document.documentElement.clientHeight / 1.5 + 'px' },
    };
    this.modalStyle = builedStyle;
  }

  ngAfterViewInit(): void {
    if (this.fileName && this.urlFile) {
      this.onPreview(null, this.fileName, this.urlFile);
    }
  }

  onPreview(event: Event, fileName: string, url: string): void {
    this.innerHtmlContent = null;
    this.hasPreviewError = false;
    this.isPreviewDisplayed = false;
    this.isDocPreview = false;
    this.isImagePreview = false;
    this.dataUrl = null;
    this.isLoading = false;
    this.isUnsupported = false;
    this.imageZoomed = false;
    this.blob = null;
    this.showError = false;
    this.sourceUrl = url;
    this.fileName = fileName;
    this.fileIconClass = getFileTypeIconClass(fileName);
    this.previewSeq++;
    this.extension = fileName ? (fileName.split('.').length > 1 ? fileName.split('.').pop() : null) : null;
    if (this.extension) {
      this.extension = this.extension.toLowerCase();
      const urlPreview = url?.replace && this.fromDocServer ? this.docServerService.getUrlPreview(url) : url;
      const mimeType = mime.getType(this.extension.toLowerCase());
      if (mimeType && mimeType.includes('image')) {
        this.isPreviewDisplayed = true;
        this.isImagePreview = true;
        this.url = urlPreview;
      } else if (this.getMediaType(this.fileName) && RENDERABLE_EXTENSIONS.includes(this.extension)) {
        this.mediaType = this.getMediaType(this.fileName);
        this.isPreviewDisplayed = true;
        this.isDocPreview = true;
        this.url = urlPreview as string;
      } else {
        // not renderable: show the fallback (icon, name, download) instead of an empty area
        this.isPreviewDisplayed = true;
        this.isUnsupported = true;
        this.url = null;
      }
      if (!this.isUnsupported) {
        this.isLoading = true;
        this.preview(this.url as string);
      }
    } else {
      this.isPreviewDisplayed = true;
      this.showError = true;
    }
    if (this.overlay) {
      this.alignPreviewFile(event as MouseEvent);
      this.opRef.show(event);
    }
  }

  getMediaType(fileName: string): string | null {
    const name = fileName.toLowerCase();
    const ext = name.split('.').pop();
    return mime.getType(ext);
  }

  // Duplicate du pipe docserverurl
  preview(src: string): void {
    const seq = this.previewSeq;
    this.docServerService.getDocument(src).subscribe((finalResponse) => {
      if (seq !== this.previewSeq) {
        return;
      }
      this.hasPreviewError = false;
      if (
        finalResponse === null ||
        finalResponse.text.includes('Could not find document:') ||
        finalResponse.response.body.size === 0
      ) {
        this.hasPreviewError = true;
        this.isLoading = false;
        this.dataUrl = null;
      } else {
        if (['txt', 'sh', 'csv', 'xlsx'].includes(this.extension)) {
          const textReader = new FileReader();

          textReader.onloadend = () => {
            if (seq !== this.previewSeq) {
              return;
            }
            // xlsx is parsed asynchronously: keep the loader until it is done
            this.isLoading = this.extension === 'xlsx';
            if (typeof textReader.result === 'string') {
              const fileContent = textReader.result;
              // Generate preview based on file type
              switch (this.extension) {
                case 'xlsx':
                  this.previewXlsx(fileContent).finally(() => {
                    if (seq === this.previewSeq) {
                      this.isLoading = false;
                    }
                  });
                  break;
                case 'csv':
                  this.previewCsv(fileContent);
                  break;
                case 'txt':
                  this.previewText(fileContent);
                  break;
                case 'sh':
                  this.previewText(fileContent);
                  break;
                default:
                  this.innerHtmlContent = this.sanitized.sanitize(
                    SecurityContext.HTML,
                    this.translateService.instant('previewDoc.noPreview')
                  );
              }
            }
            if (this?.parentComponent?.scrollTo) {
              setTimeout(() => {
                this.parentComponent.scrollTo();
              }, 10);
            }
          };

          textReader.readAsBinaryString(finalResponse.response.body);
        } else {
          //img, pdf, xml
          const reader = new FileReader();
          reader.onloadend = (evt) => {
            if (seq !== this.previewSeq) {
              return;
            }
            this.isLoading = false;
            const base64 = evt.target.result as string;
            const fileName = this.fileName;
            let ext = this.extension;
            let mimeType = 'image/png';
            if (ext) {
              ext = ext.toLocaleLowerCase();
              mimeType = mime.getType(ext);
            }
            if (fileName && base64.match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/)) {
              const mimeTypeToReplace = base64.match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/)[0];
              this.dataUrl = base64.replace(mimeTypeToReplace, mimeType);
            } else {
              if (!mimeType.includes('image')) {
                this.dataUrl = null;
              } else {
                this.dataUrl = this.sanitized.bypassSecurityTrustUrl('assets/imageError.webp');
              }
            }
            if (this?.parentComponent?.scrollTo) {
              setTimeout(() => {
                this.parentComponent.scrollTo();
              }, 10);
            }
          };
          reader.readAsDataURL(finalResponse.response.body as Blob);
        }

        this.blob = finalResponse.response.body;
      }
    });
  }

  /** Downloads the current file (original document when it comes from the doc server). */
  download(): void {
    const name = this.fileName || 'document';
    const src = this.sourceUrl;
    if (typeof src === 'string' && this.fromDocServer) {
      this.docServerService.downloadDocument(src).subscribe({
        next: (response) => Global.downloadFile(response as any, name),
        error: () => this.blob && this.saveObjectUrl(URL.createObjectURL(this.blob), name, true),
      });
    } else if (this.blob) {
      this.saveObjectUrl(URL.createObjectURL(this.blob), name, true);
    } else if (src) {
      const href = typeof src === 'string' ? src : this.sanitized.sanitize(SecurityContext.RESOURCE_URL, src);
      if (href) {
        this.saveObjectUrl(href, name, false);
      }
    }
  }

  get canDownload(): boolean {
    return !!(this.sourceUrl || this.blob);
  }

  get canOpenInNewTab(): boolean {
    return !!this.blob && !this.isLoading && !this.hasPreviewError;
  }

  /** Opens the loaded file in a new browser tab, typed from its extension so the browser can render it. */
  openInNewTab(): void {
    if (!this.blob) {
      return;
    }
    const type = (this.extension && mime.getType(this.extension)) || this.blob.type;
    const objectUrl = URL.createObjectURL(new Blob([this.blob], { type }));
    window.open(objectUrl, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  }

  toggleImageZoom(): void {
    this.imageZoomed = !this.imageZoomed;
  }

  get fileSizeLabel(): string | null {
    const size = this.blob?.size;
    if (!size) {
      return null;
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  private saveObjectUrl(href: string, name: string, revoke: boolean): void {
    const link = document.createElement('a');
    link.href = href;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    if (revoke) {
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    }
  }

  async previewXlsx(fileContent: string) {
    const workbook = await this.xlsxParser.readExcel(fileContent, { type: 'binary' });
    let html = '';
    // Extract the content from each sheet
    workbook.eachSheet((sheet, sheetId) => {
      html += `<h3>${sheet.name}</h3>`;
      const sheetData = [];
      const columnData = {};
      sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        const rowData = {};
        if (rowNumber == 1) {
          row.eachCell((cell, colNumber) => {
            columnData[colNumber] = cell.value;
          });
        } else {
          row.eachCell((cell, colNumber) => {
            rowData[`${columnData[colNumber]}`] = cell.value;
          });
          sheetData.push(rowData);
        }
      });
      html += this.generateTableFromJson(sheetData);
    });
    this.innerHtmlContent = this.sanitized.sanitize(SecurityContext.HTML, html);
  }

  generateTableFromJson(lines: object[]): string {
    let html = '<table>';

    // Add table header
    html += '<thead><tr>';
    const headers = Object.keys(lines[0]);

    for (const header of headers) {
      html += `<th >${header}</th>`;
    }
    html += '</tr></thead>';

    // Add table body
    html += '<tbody>';
    for (const line of lines) {
      const values = Object.keys(line);
      html += `<tr">`;
      for (const value of values) {
        html += `<td>${line[value]}</td>`;
      }
      html += '</tr>';
    }
    html += '</tbody>';

    html += '</table>';
    return html;
  }

  previewCsv(fileContent: string): void {
    this.innerHtmlContent = this.sanitized.sanitize(SecurityContext.HTML, this.generateTable(fileContent));
  }

  generateTable(fileContent: string, delimiter = ';') {
    const lines = fileContent.split('\n');
    let html = '<table>';

    // Add table header
    html += '<thead><tr>';
    const headers = lines[0].split(delimiter);
    if (headers.length != 1 || headers[0] != '') {
      for (const header of headers) {
        html += `<th>${header}</th>`;
      }
      html += '</tr></thead>';
    }
    // Add table body
    html += '<tbody>';
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(delimiter);
      if (i == 1 && values.length != headers.length) {
        if (delimiter === ',') {
          return '';
        }
        return this.generateTable(fileContent, ',');
      }
      if (values.length != 1 || values[0] != '') {
        html += `<tr> `;
        for (const value of values) {
          html += `<td>${value}</td>`;
        }
        html += '</tr>';
      }
    }
    html += '</tbody>';

    html += '</table>';
    return html;
  }

  previewText(fileContent: string): void {
    this.innerHtmlContent = this.sanitized.sanitize(SecurityContext.HTML, `<pre>${fileContent}</pre>`);
  }

  setParam(base64: string, options = {}) {
    const defaultOptions = { toolbar: 0, scrollbar: 0, navpanes: 0, zoom: '80, 0, -20' };
    const { toolbar, scrollbar, navpanes, zoom } = { ...defaultOptions, ...options };
    return `${base64}#toolbar=${toolbar}&navpanes=${navpanes}&scrollbar=${scrollbar}&zoom=${zoom};`;
  }

  alignPreviewFile({ x, y, target }: MouseEvent) {
    /* Preview size */
    const pWidth = 40;
    const pHeight = 48;
    /* Alignement offset */
    const offset = 10;
    /* viewport width */
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    /* Target position */
    const targetBCR = (target as HTMLElement).getBoundingClientRect();
    const left = `${Math.floor(vw / 2) >= x ? targetBCR.left : targetBCR.right - Math.floor(vw * (pWidth / 100))}px`;
    const top = `${
      Math.floor(vh / 2) >= y ? targetBCR.bottom : targetBCR.top - Math.floor(vh * (pHeight / 100)) - offset
    }px`;
    /* Override prime overlay panel style  */
    this.overridePreviewPanelStyle = [
      ['position', 'absolute'],
      ['width', `${pWidth}%`],
      ['height', `${pHeight}%`],
      ['top', top],
      ['right', 'auto'],
      ['bottom', 'auto'],
      ['left', left],
    ];
  }
  handleAlign(context: PrimeOverlayComponent) {
    if (context?.overlayVisible) {
      for (const [key, value] of this.overridePreviewPanelStyle) {
        context.container.style[key] = value;
      }
    }
  }
}
